import { env } from "@/lib/env";
import { tokenStore } from "@/features/auth/token-store";

/**
 * Thin typed wrapper over fetch — the ONLY place HTTP happens on the
 * frontend. Feature `api.ts` files call these helpers; components never
 * call fetch directly.
 *
 * Responsibilities:
 *  - prefix the API base URL
 *  - attach the in-memory access token
 *  - send cookies (refresh token is an httpOnly cookie)
 *  - on 401: attempt one silent refresh, then replay the request
 *  - normalize errors into ApiError with the server's error envelope
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  /** Skip the automatic 401 → refresh → retry cycle (used by auth itself). */
  skipRefresh?: boolean;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, skipRefresh, headers, ...init } = options;

  const doFetch = () =>
    fetch(`${env.VITE_API_BASE_URL}${path}`, {
      ...init,
      credentials: "include",
      headers: {
        ...(body !== undefined && !(body instanceof FormData)
          ? { "Content-Type": "application/json" }
          : {}),
        ...(tokenStore.get() ? { Authorization: `Bearer ${tokenStore.get()}` } : {}),
        ...headers,
      },
      body:
        body === undefined
          ? undefined
          : body instanceof FormData
            ? body
            : JSON.stringify(body),
    });

  let res = await safeFetch(doFetch);

  if (res.status === 401 && !skipRefresh) {
    const refreshed = await tokenStore.refresh();
    if (refreshed) res = await safeFetch(doFetch);
  }

  if (!res.ok) {
    const payload = (await res.json().catch(() => null)) as {
      error?: { code?: string; message?: string; details?: unknown };
    } | null;
    throw new ApiError(
      res.status,
      payload?.error?.code ?? "UNKNOWN",
      normalizeErrorMessage(res.status, payload?.error?.message, res.statusText),
      payload?.error?.details,
    );
  }

  if (res.status === 204) return undefined as T;
  const json = (await res.json()) as { data: T };
  return json.data;
}

async function safeFetch(fetcher: () => Promise<Response>) {
  try {
    return await fetcher();
  } catch {
    throw new ApiError(
      0,
      "NETWORK_ERROR",
      "Unable to reach the API. Check your connection and try again.",
    );
  }
}

function normalizeErrorMessage(status: number, message?: string, statusText?: string) {
  if (message && message !== "Internal Server Error") return message;
  if (status === 400) return "The request could not be processed. Check the form and try again.";
  if (status === 401) return "Please sign in to continue.";
  if (status === 403) return "You do not have permission to perform this action.";
  if (status === 404) return "The requested content was not found.";
  if (status === 409) return "That value conflicts with an existing record.";
  if (status === 422) return "Some submitted fields need attention.";
  if (status === 429) return "Too many requests. Please wait a moment and try again.";
  if (status === 503) return "The site is temporarily unavailable for maintenance.";
  if (status >= 500) return "The server hit an unexpected error. Please try again.";
  return statusText || "Something went wrong. Please try again.";
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: "GET" }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: "POST", body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: "PATCH", body }),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: "DELETE" }),
};
